<template>
  <div class="cert-form">
    <div class="section">
      <div class="section-title">Identification</div>
      <div class="grid grid-2">
        <label class="field">
          <span class="field-label">Performed by</span>
          <dg-input v-model="form.performedByName" placeholder="Analyst name" />
        </label>
        <label class="field">
          <span class="field-label">Phone</span>
          <dg-input v-model="form.performedByPhone" placeholder="+45 ..." />
        </label>
        <label class="field">
          <span class="field-label">Certified at <span class="required">*</span></span>
          <el-date-picker
            v-model="form.certifiedAt"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="Pick date"
            @change="onCertifiedAtChange"
            style="width: 100%;" />
        </label>
        <label class="field">
          <span class="field-label">Expires at <span class="required">*</span></span>
          <el-date-picker
            v-model="form.expiresAt"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="Pick date"
            @change="onExpiresAtChange"
            style="width: 100%;" />
        </label>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Compressor</div>
      <div class="grid grid-2">
        <label class="field">
          <span class="field-label">Model</span>
          <dg-input v-model="form.compressorModel" placeholder="e.g. Bauer Mariner" />
        </label>
        <label class="field">
          <span class="field-label">Build year</span>
          <dg-input v-model="form.compressorBuildYear" placeholder="e.g. 2018" />
        </label>
        <label class="field">
          <span class="field-label">Compressor hours</span>
          <el-input-number v-model="form.compressorHours" :precision="1" :step="1" :min="0" controls-position="right" size="small" />
        </label>
        <label class="field">
          <span class="field-label">Analysis equipment</span>
          <dg-input v-model="form.analysisEquipment" placeholder="e.g. Dräger Aerotest" />
        </label>
        <label class="field">
          <span class="field-label">Last filter change at</span>
          <el-date-picker
            v-model="form.lastFilterChangeAt"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="Pick date"
            style="width: 100%;" />
        </label>
        <label class="field">
          <span class="field-label">Hours at last filter change</span>
          <el-input-number v-model="form.lastFilterChangeHours" :precision="1" :step="1" :min="0" controls-position="right" size="small" />
        </label>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Sample</div>
      <div class="grid grid-2">
        <label class="field">
          <span class="field-label">Source <span class="required">*</span></span>
          <el-radio-group v-model="form.sampleSource">
            <el-radio label="FromCompressor">From compressor</el-radio>
            <el-radio label="FromCylinder">From cylinder</el-radio>
          </el-radio-group>
        </label>
        <label v-if="form.sampleSource === 'FromCylinder'" class="field">
          <span class="field-label">Cylinder pressure (bar)</span>
          <el-input-number v-model="form.cylinderPressureBar" :precision="0" :step="10" :min="0" controls-position="right" size="small" />
        </label>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Measurements</div>
      <table class="measurement-table">
        <thead>
          <tr>
            <th>Component</th>
            <th>Limit</th>
            <th>Measured</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Water (mg/m³)</td>
            <td><el-input-number v-model="form.waterLimitMgPerM3" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td><el-input-number v-model="form.waterMgPerM3" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td></td>
          </tr>
          <tr>
            <td>Oil (mg/m³)</td>
            <td><el-input-number v-model="form.oilLimitMgPerM3" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td><el-input-number v-model="form.oilMgPerM3" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td></td>
          </tr>
          <tr>
            <td>CO (ppm)</td>
            <td><el-input-number v-model="form.coLimitPpm" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td><el-input-number v-model="form.coPpm" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td></td>
          </tr>
          <tr>
            <td>CO₂ (ppm)</td>
            <td><el-input-number v-model="form.co2LimitPpm" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td><el-input-number v-model="form.co2Ppm" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td></td>
          </tr>
          <tr>
            <td>NOx (ppm)</td>
            <td><el-input-number v-model="form.noxLimitPpm" :precision="2" :step="0.1" :min="0" controls-position="right" size="mini" /></td>
            <td>
              <el-input-number
                v-model="form.noxPpm"
                :precision="2"
                :step="0.1"
                :min="0"
                :disabled="!form.noxMeasured"
                controls-position="right"
                size="mini" />
            </td>
            <td>
              <el-switch v-model="form.noxMeasured" active-text="Measured" inactive-text="" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title">Document</div>
      <div v-if="documentFile" class="current-doc">
        <a :href="documentFile.path" target="_blank" rel="noopener" class="doc-link">
          <i class="el-icon-document"></i>
          View current document
        </a>
        <el-button type="text" size="mini" icon="el-icon-refresh" @click="onFileRemove">Replace</el-button>
      </div>
      <document-uploader
        v-else
        label="Upload analyst PDF"
        @uploadStart="onUploadStart"
        @uploadSuccess="onUploadSuccess"
        @uploadError="onUploadError"
        @input="onFileId" />
    </div>

    <div class="section">
      <div class="section-title">Notes</div>
      <el-input
        v-model="form.notes"
        type="textarea"
        :rows="3"
        placeholder="Free-text remarks from the report" />
    </div>

    <div v-if="validationMessage" class="validation">{{ validationMessage }}</div>

    <div class="actions">
      <el-button @click="$emit('cancel')">Cancel</el-button>
      <el-button
        type="primary"
        icon="el-icon-upload"
        :loading="saving"
        :disabled="!canSubmit"
        @click="handleSave">
        Save
      </el-button>
    </div>
  </div>
</template>

<script>
import DocumentUploader from './DocumentUploader'

function todayIso () {
  return new Date().toISOString().slice(0, 10)
}

function addDaysIso (iso, days) {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

const SAMPLE_SOURCE_NAMES = { 0: 'FromCylinder', 1: 'FromCompressor' }

function normalizeSampleSource (value) {
  if (typeof value === 'string' && (value === 'FromCylinder' || value === 'FromCompressor')) return value
  if (typeof value === 'number' && SAMPLE_SOURCE_NAMES[value]) return SAMPLE_SOURCE_NAMES[value]
  return 'FromCompressor'
}

function emptyForm () {
  const certifiedAt = todayIso()
  return {
    performedByName: '',
    performedByPhone: '',
    certifiedAt,
    expiresAt: addDaysIso(certifiedAt, 365),
    compressorModel: '',
    compressorBuildYear: '',
    compressorHours: null,
    analysisEquipment: '',
    lastFilterChangeAt: null,
    lastFilterChangeHours: null,
    sampleSource: 'FromCompressor',
    cylinderPressureBar: null,
    waterMgPerM3: null,
    waterLimitMgPerM3: null,
    oilMgPerM3: null,
    oilLimitMgPerM3: null,
    coPpm: null,
    coLimitPpm: null,
    co2Ppm: null,
    co2LimitPpm: null,
    noxPpm: null,
    noxLimitPpm: null,
    noxMeasured: true,
    fileId: null,
    notes: ''
  }
}

export default {
  name: 'CertificationForm',
  components: { DocumentUploader },
  props: {
    station: {
      type: Object,
      required: true
    },
    certification: {
      type: Object,
      required: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      form: emptyForm(),
      expiresAtTouched: false,
      uploading: false,
      saving: false,
      documentFile: null
    }
  },
  computed: {
    validationMessage () {
      if (!this.form.certifiedAt) return 'Certified at is required.'
      if (!this.form.expiresAt) return 'Expires at is required.'
      if (this.form.expiresAt <= this.form.certifiedAt) {
        return 'Expires at must be after certified at.'
      }
      if (!this.form.sampleSource) return 'Sample source is required.'
      return null
    },
    canSubmit () {
      return !this.validationMessage && !this.uploading && !this.saving
    }
  },
  methods: {
    onCertifiedAtChange () {
      if (!this.expiresAtTouched && this.form.certifiedAt) {
        this.form.expiresAt = addDaysIso(this.form.certifiedAt, 365)
      }
    },
    onExpiresAtChange () {
      this.expiresAtTouched = true
    },
    onUploadStart () {
      this.uploading = true
    },
    onUploadSuccess (file) {
      this.uploading = false
      this.documentFile = file
      this.form.fileId = file.id
    },
    onUploadError () {
      this.uploading = false
    },
    onFileId (id) {
      this.form.fileId = id
    },
    onFileRemove () {
      this.documentFile = null
      this.form.fileId = null
    },
    buildPayload () {
      const payload = { ...this.form }
      if (payload.sampleSource === 'FromCompressor') {
        payload.cylinderPressureBar = null
      }
      if (payload.noxMeasured === false) {
        payload.noxPpm = null
      }
      return payload
    },
    async handleSave () {
      if (!this.canSubmit) return
      this.saving = true
      try {
        const sId = this.$route.params.sId
        const payload = this.buildPayload()
        if (this.editing && this.certification) {
          const url = this.$api.station(sId).certification(this.certification.id).update
          const response = await this.$http.put(url, payload)
          this.$message.success('Certification updated')
          this.$emit('updated', response.data)
        } else {
          const url = this.$api.station(sId).certifications
          const response = await this.$http.post(url, payload)
          this.$message.success('Certification created')
          this.$emit('created', response.data)
        }
      } catch (e) {
        this.$message.error(this.errorMessage(e) || 'Could not save certification')
      } finally {
        this.saving = false
      }
    },
    errorMessage (e) {
      if (e && e.response && e.response.data) {
        if (typeof e.response.data === 'string') return e.response.data
        if (e.response.data.message) return e.response.data.message
      }
      return null
    }
  },
  created () {
    if (this.editing && this.certification) {
      const c = this.certification
      this.form = {
        performedByName: c.performedByName || '',
        performedByPhone: c.performedByPhone || '',
        certifiedAt: c.certifiedAt ? c.certifiedAt.slice(0, 10) : todayIso(),
        expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : addDaysIso(todayIso(), 365),
        compressorModel: c.compressorModel || '',
        compressorBuildYear: c.compressorBuildYear || '',
        compressorHours: c.compressorHours != null ? c.compressorHours : null,
        analysisEquipment: c.analysisEquipment || '',
        lastFilterChangeAt: c.lastFilterChangeAt ? c.lastFilterChangeAt.slice(0, 10) : null,
        lastFilterChangeHours: c.lastFilterChangeHours != null ? c.lastFilterChangeHours : null,
        sampleSource: normalizeSampleSource(c.sampleSource),
        cylinderPressureBar: c.cylinderPressureBar != null ? c.cylinderPressureBar : null,
        waterMgPerM3: c.waterMgPerM3,
        waterLimitMgPerM3: c.waterLimitMgPerM3,
        oilMgPerM3: c.oilMgPerM3,
        oilLimitMgPerM3: c.oilLimitMgPerM3,
        coPpm: c.coPpm,
        coLimitPpm: c.coLimitPpm,
        co2Ppm: c.co2Ppm,
        co2LimitPpm: c.co2LimitPpm,
        noxPpm: c.noxPpm,
        noxLimitPpm: c.noxLimitPpm,
        noxMeasured: c.noxMeasured !== false,
        fileId: c.documentFile ? c.documentFile.id : null,
        notes: c.notes || ''
      }
      this.documentFile = c.documentFile || null
      this.expiresAtTouched = true
    }
  }
}
</script>

<style lang="scss" scoped>
  .cert-form {
    padding: 10px 0;
  }

  .section {
    margin-bottom: 25px;
  }

  .section-title {
    font-size: 12px;
    text-transform: uppercase;
    color: $text-light;
    margin-bottom: 10px;
    border-bottom: 1px solid $border;
    padding-bottom: 4px;
  }

  .grid {
    display: grid;
    gap: 12px;

    &.grid-2 {
      grid-template-columns: 1fr 1fr;
    }
  }

  .field {
    display: flex;
    flex-direction: column;

    .field-label {
      font-size: 11px;
      color: $text-light;
      margin-bottom: 4px;
    }

    .required {
      color: #FD6F71;
    }
  }

  .measurement-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      padding: 6px 8px;
      text-align: left;
      font-size: 12px;
      border-bottom: 1px solid $border;
    }

    th {
      color: $text-light;
      font-weight: 500;
      text-transform: uppercase;
    }
  }

  .validation {
    color: #e6a23c;
    font-size: 12px;
    padding: 8px 0;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;

    > * + * {
      margin-left: 10px;
    }
  }

  .current-doc {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid $border;
    border-radius: 4px;
    background-color: #fafafa;

    .doc-link {
      display: inline-flex;
      align-items: center;
      flex: 1;
      color: #008fcc;
      text-decoration: none;
      font-size: 13px;

      i {
        margin-right: 6px;
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/station/Certification/CertificationForm.vue